import { Box, Typography, useTheme } from "@outschool/backpack";
import { ClassDetailsQuery_ActivityFragment } from "@outschool/gql-frontend-generated";
import {
  Trans,
  useLocalPriceDisplay,
  useTranslation,
} from "@outschool/localization";
import React from "react";

import { useEsaUserWithoutPaidMembership } from "../../../hooks/useEsaUserWithoutPaidMembership";
import { FreeForMembersChip } from "../../Chips/FreeForMembersChip";
import { CoinIcon } from "../../subscriptions/CoinIcon";
import EstimatedValueLineItem from "../mainBody/desktopActivityCards/EstimatedValueLineItem";

// NOTE: Keep this in sync with EstimatedValueLineItem.
export default function PriceCreditsLineItem({
  activity,
  displayFullPriceOption = true,
}: {
  activity: ClassDetailsQuery_ActivityFragment;
  displayFullPriceOption?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\subcomponents\\PriceCreditsLineItem",
  );
  const theme = useTheme();
  const { makePerMeetingPriceCreditsText } = useLocalPriceDisplay();
  const { isEsaUserWithoutPaidMembership } = useEsaUserWithoutPaidMembership();

  if (!activity.priceCredits) {
    return null;
  }
  let text;
  if (activity.hasTeacherSchedule) {
    text = (
      <>
        <CreditsIcon />
        <Trans t={t}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {{ perMeetingPriceCredits: activity.priceCredits } as any}
          </Typography>
          <Typography> credits per session</Typography>
        </Trans>
      </>
    );
  } else if (activity.isOneTime) {
    text = (
      <>
        <CreditsIcon />
        <Trans t={t}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {{ perMeetingPriceCredits: activity.priceCredits } as any}
          </Typography>
          <Typography> credits per class</Typography>
        </Trans>
      </>
    );
  } else if (activity.isClub) {
    text = (
      <>
        <CreditsIcon />
        <Trans t={t}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {{ perMonthPriceCredits: activity.priceCredits } as any}
          </Typography>
          <Typography> credits monthly</Typography>
        </Trans>
      </>
    );
  } else if (activity.isSelfPaced && activity.duration_weeks) {
    if (activity.isIncludedWithMembership && !isEsaUserWithoutPaidMembership) {
      text = (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <FreeForMembersChip />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "baseline",
              whiteSpace: "pre-wrap",
              flexWrap: "wrap",
            }}
          >
            <CreditsIcon />
            <Trans t={t}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                0
              </Typography>
              <Typography> credits</Typography>
            </Trans>
          </Box>
          <EstimatedValueLineItem activity={activity} />
        </Box>
      );
    } else {
      if (activity.duration_weeks === 1) {
        text = (
          <>
            <CreditsIcon />
            <Trans t={t}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {{ perCourseCredits: activity.priceCredits } as any}
              </Typography>
              <Typography> credits for all content</Typography>
            </Trans>
          </>
        );
      } else {
        text = (
          <>
            <CreditsIcon />
            <Trans t={t}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {
                  {
                    perWeeklyCourseCredits: activity.perWeeklyCourseCredits,
                  } as any
                }
              </Typography>
              <Typography> credits weekly</Typography>
              {displayFullPriceOption && (
                <Box
                  sx={{
                    paddingLeft: "3rem",
                    color: theme.palette.text.secondary,
                  }}
                >
                  <Typography>
                    or {{ perCourseCredits: activity.priceCredits } as any}{" "}
                    credits for all content
                  </Typography>
                </Box>
              )}
            </Trans>
          </>
        );
      }
    }
  } else if (activity.is_ongoing_weekly && activity.weekly_meetings === 1) {
    text = (
      <>
        <CreditsIcon />
        <Trans t={t}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {{ weeklyPrice: activity.priceCredits } as any}
          </Typography>
          <Typography> credits weekly</Typography>
        </Trans>
      </>
    );
  } else if (
    activity.is_ongoing_weekly &&
    (activity.weekly_meetings || 0) > 1
  ) {
    text = (
      <>
        <CreditsIcon />
        <Trans t={t}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {{ weeklyPrice: activity.priceCredits } as any}
          </Typography>
          <Typography> credits weekly</Typography>
          {displayFullPriceOption && (
            <>
              <Typography> (</Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {
                  {
                    perMeetingPrice: makePerMeetingPriceCreditsText(activity),
                  } as any
                }
              </Typography>
              <Typography> credits per class)</Typography>
            </>
          )}
        </Trans>
      </>
    );
  } else if (!activity.allows_recurring_payment) {
    text = (
      <>
        <CreditsIcon />
        <Trans t={t}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {{ perCourseCredits: activity.priceCredits } as any}
          </Typography>
          <Typography>
            {" "}
            credits for{" "}
            {{
              classes:
                (activity.duration_weeks || 0) *
                (activity.weekly_meetings || 0),
            }}{" "}
            classes
          </Typography>
        </Trans>
      </>
    );
  } else if (
    activity.allows_recurring_payment &&
    activity.duration_weeks &&
    activity.perWeeklyCourseCredits
  ) {
    text = (
      <>
        <CreditsIcon />
        <Trans t={t}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {
              {
                perWeeklyCourseCredits: activity.perWeeklyCourseCredits,
              } as any
            }
          </Typography>
          <Typography> credits weekly</Typography>
          {displayFullPriceOption && (
            <Box
              sx={{ paddingLeft: "3rem", color: theme.palette.text.secondary }}
            >
              <Typography>
                or {{ perCourseCredits: activity.priceCredits } as any} credits
                for{" "}
                {{
                  classes:
                    (activity.duration_weeks || 0) *
                    (activity.weekly_meetings || 0),
                }}{" "}
                classes
              </Typography>
            </Box>
          )}
        </Trans>
      </>
    );
  }
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        whiteSpace: "pre-wrap",
        flexWrap: "wrap",
      }}
    >
      {text}
    </Box>
  );
}

const CreditsIcon = () => {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", alignSelf: "center", marginRight: 4 }}>
      <CoinIcon
        fillColor={theme.palette.warning.main}
        strokeColor={theme.palette.warning[700]}
      />
    </Box>
  );
};
